import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Pagination } from "../../../../shared/ui/common/Pagination";
import { Trash2, Pencil, ClipboardList } from "lucide-react";
import { Link, useNavigate } from "react-router";
import { deleteTherapeuticPlan, listTherapistPlans } from "../../application/therapeuticPlans";

import { TherapeuticPlan } from "../../domain/TherapeuticPlan";

interface TherapeuticPlansListProps {
  therapistID: string;
  filters?: {
    patientName?: string;
    planType?: string;
  };
  pageSize?: number;
}

export default function TherapeuticPlanList({ therapistID, filters, pageSize = 10 }: TherapeuticPlansListProps) {
  const { t } = useTranslation();
  const [therapeuticPlans, setTherapeuticPlans] = useState<TherapeuticPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  const navigate = useNavigate();

  const fetchTherapeuticPlans = useCallback(async () => {
    if (!therapistID) return;
    try {
      setLoading(true);

      const params: Record<string, unknown> = {
        offset,
        limit: pageSize,
      };

      if (filters?.patientName) params.patient_name = filters.patientName;
      if (filters?.planType) params.plan_type = filters.planType;

      const page = await listTherapistPlans(therapistID, params);

      const data = page.therapeutic_plans;
      setTherapeuticPlans(data);
      setTotal(page.total);
    } catch (err) {
      console.error("Error fetching therapeutic plans:", err);
      setError(t("therapeuticPlan.errorLoading"));
    } finally {
      setLoading(false);
    }
  }, [therapistID, offset, pageSize, filters, t]);

  useEffect(() => {
    fetchTherapeuticPlans();
  }, [fetchTherapeuticPlans]);

  // 🔹 Reiniciar la paginación al cambiar el filtro
  useEffect(() => {
    setOffset(0);
  }, [filters?.patientName, filters?.planType]);

  const handleDelete = async (id: string) => {
    if (!confirm(t("therapeuticPlan.deleteConfirm"))) return;
    setDeletingId(id);
    try {
      await deleteTherapeuticPlan(id);
      
      // Si era el último elemento de la página y no estamos en la primera, retrocedemos
      if (therapeuticPlans.length === 1 && offset > 0) {
        setOffset((prev) => Math.max(prev - pageSize, 0));
      } else {
        // Si no, simplemente refrescamos para actualizar la lista y el total
        fetchTherapeuticPlans();
      }
    } catch (err) {
      console.error("Error deleting therapeutic plan:", err);
      alert(t("therapeuticPlan.deleteError"));
    } finally {
      setDeletingId(null);
    }
  };

  const handleEdit = (therapeuticPlan: TherapeuticPlan) => {
    navigate(`/therapeutic-plans/${therapeuticPlan.id}`);
  };

  // 🔹 Controles de paginación
  const handlePrevPage = () => setOffset((prev) => Math.max(prev - pageSize, 0));
  const handleNextPage = () => setOffset((prev) => prev + pageSize);


  // 🔹 Renderizado condicional
  if (loading) {
    return (
      <div className="flex justify-center py-10 text-content-tertiary">
        {t("therapeuticPlan.loading")}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center text-red-500 dark:text-red-400 py-10">
        {error}
      </div>
    );
  }

  if (!therapeuticPlans.length) {
    return (
      <div className="p-10 text-center bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700">
        <p className="text-content-tertiary">{t("therapeuticPlan.notFound")}</p>
      </div>
    );
  }

  // 🔹 Listado de planes terapéuticos (Cards)
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        {therapeuticPlans.map((tp) => (
          <div
            key={tp.id}
            className="group relative flex flex-col sm:flex-row items-center justify-between p-4 bg-white border border-gray-200 rounded-2xl shadow-theme-xs hover:shadow-theme-md transition-all duration-200 dark:bg-gray-900 dark:border-gray-800"
          >
            <div className="flex flex-1 items-center gap-4 w-full">
              {/* Patient Avatar (representing the plan context) */}
              <div
                className={`flex items-center justify-center w-12 h-12 rounded-full shrink-0 ${tp.couple_patient_id
                  ? "bg-zen-pink/10 text-zen-pink"
                  : "bg-zen-blue/10 text-zen-blue"
                  }`}
                title={tp.couple_patient_id ? tp.patient_name || "" : `${tp.patient_name || ""} ${tp.patient_last_name || ""}`}
              >
                <ClipboardList className="w-5 h-5 opacity-80" />
              </div>

              {/* Plan Details */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/therapeutic-plans/${tp.id}`}
                    className="group-hover:text-brand-500 transition-colors min-w-0"
                  >
                    <h3 className="text-base font-semibold text-content-primary truncate">
                      {tp.title || t("therapeuticPlan.untitled")}
                    </h3>
                  </Link>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${tp.couple_patient_id
                    ? 'bg-brand-50 text-brand-600 border border-brand-100 dark:bg-brand-500/10 dark:text-brand-400 dark:border-brand-500/20'
                    : 'bg-gray-50 text-gray-600 border border-gray-100 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'
                    }`}>
                    {tp.couple_patient_id ? t('common.couple') : t('common.individual')}
                  </span>
                </div>
                <div className="mt-1 text-sm text-content-tertiary">
                  <Link
                    to={tp.couple_patient_id ? `/couple-patient/${tp.couple_patient_id}` : `/patient/${tp.patient_id}`}
                    className="hover:text-brand-500 transition-colors font-medium text-brand-600 dark:text-brand-400 block truncate"
                  >
                    {tp.patient_name} {tp.patient_last_name}
                  </Link>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 mt-4 sm:mt-0 ml-auto sm:ml-4">
              <button
                className="inline-flex items-center justify-center p-2.5 rounded-xl bg-gray-50 text-gray-500 hover:bg-brand-50 hover:text-brand-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-brand-500/10 dark:hover:text-brand-400 transition-all"
                onClick={() => handleEdit(tp)}
                title={t("common.edit")}
              >
                <Pencil className="w-5 h-5" />
              </button>
              <button
                className="inline-flex items-center justify-center p-2.5 rounded-xl bg-gray-50 text-gray-500 hover:bg-red-50 hover:text-red-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-red-500/10 dark:hover:text-red-400 transition-all"
                onClick={() => handleDelete(tp.id)}
                disabled={deletingId === tp.id}
                title={t("common.delete")}
              >
                {deletingId === tp.id ? (
                  <span className="w-5 h-5 flex items-center justify-center text-xs animate-pulse">...</span>
                ) : (
                  <Trash2 className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* 🔹 Footer de paginación */}
      <Pagination
        offset={offset}
        total={total}
        pageSize={pageSize}
        onPrev={handlePrevPage}
        onNext={handleNextPage}
      />
    </div>
  );
}
